import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Server, Laptop, Monitor, HardDrive, Database, UserCircle, Shield, Cpu, AlertTriangle, ChevronDown, ChevronRight, Zap, Lock, Activity, Briefcase, Calendar, Clock, Mail, Phone, Users, Sunrise, Sunset, Moon, Coffee, Wifi } from 'lucide-react';

const DataWorkflow = () => {
  // State to track data flow animation
  const [activeFlow, setActiveFlow] = useState<number>(0);

  // State for real-time stat updates
  const [stats, setStats] = useState({
    logsProcessed: 1457834,
    totalEndpointsCapacity: 100,
    threatEvents: 17,
    logsPerSecond: 12743,
    diskUsage: 76.4,
    cpuLoad: 38.2
  });

  // State for team member details modal
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);

   // Team members data
  const teamMembers = {
    morning: [
      { 
        id: 1, 
        name: 'Team Member 1', 
        shift: 'Morning',
        email: 'example@abhi.com.pk', 
        phone: '+9230-40000033',
        avatar: 'https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/female.png&auto=format&fit=crop&w=256&q=80',
        role: 'SOC Analyst'
      },
      { 
        id: 2, 
        name: 'Team Member 2', 
        shift: 'Morning',
        email: 'example@abhi.com.pk', 
        phone: '+92333-434343535',
        avatar: 'https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/female.png&auto=format&fit=crop&w=256&q=80',
        role: 'SOC Analyst'
      },
      { 
        id: 3, 
        name: 'Team Member 3', 
        shift: 'Morning',
        email: 'example@abhi.com.pk', 
        phone: '+92300-33323232',
        avatar: 'https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/female.png&auto=format&fit=crop&w=256&q=80',
        role: 'SOC Analyst'
      }
    ],
    evening: [
      { 
        id: 4, 
        name: 'Team Member 4', 
        shift: 'Evening',
        email: 'example@abhi.com.pk', 
        phone: '+92303-0099331',
        avatar: 'https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/usama-ahmad.png&auto=format&fit=crop&w=256&q=80',
        role: 'SOC Analyst'
      },
      { 
        id: 5, 
        name: 'Team Member 5', 
        shift: 'Evening',
        email: 'zaeem@abhi.com.pk', 
        phone: '+92323-6878242',
        avatar: 'https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/zaeem.png&auto=format&fit=crop&w=256&q=80',
        role: 'SOC Analyst'
      }
    ],
    night: [
      { 
        id: 6, 
        name: 'Team Member 6', 
        shift: 'Night',
        email: 'example@abhi.com.pk', 
        phone: '+92340-0876245',
        avatar: 'https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/usama.png&auto=format&fit=crop&w=256&q=80',
        role: 'SOC Analyst'
      },
      { 
        id: 7, 
        name: 'Team Member 7', 
        shift: 'Night',
        email: 'example@abhi.com.pk', 
        phone: '+92302-6756414',
        avatar: 'https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/asad.png&auto=format&fit=crop&w=256&q=80',
        role: 'SOC Analyst'
      }
    ],
    head: { 
      id: 8, 
      name: 'Team Lead', 
      shift: '24/7',
      email: 'example@abhi.com.pk', 
      phone: '+923068756234',
      avatar: 'https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/talha.png&auto=format&fit=crop&w=256&q=80',
      role: 'Team Lead of SOC'
    }
  };
  // Simulated data for node activity and status
  const nodes = {
    endpoints: [
      { id: 'server1', type: 'Server', status: 'active', logCount: 9478 },
      { id: 'desktop1', type: 'Desktop', status: 'active', logCount: 3215 },
      { id: 'laptop1', type: 'Laptop', status: 'active', logCount: 2187 },
      { id: 'vm1', type: 'Virtual Machine', status: 'active', logCount: 5632 }
    ],
    AbhiStrikeWorkers: [
      { id: 'master1', name: 'AbhiStrike-Master', ip: '10.10.10.6', status: 'operational', load: 42 }
    ],
    master: {
      id: 'master1', 
      name: 'Abhi-G1',
      ip: '10.10.10.6',
      status: 'operational',
      load: 42,
      dataRate: '142.7 MB/s'
    },
    indexers: [
      { id: 'indexer1', name: 'AbhiStrike-Indexer-1', ip: '10.10.10.6', status: 'operational', indexRate: '45.2 K/s' },
    ],
    dashboard: {
      id: 'dashboard1',
      name: 'AbhiStrike-Dashboard',
      ip: '10.10.10.6',
      status: 'operational',
      activeUsers: 2
    }
  };

  // Update stats periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prevStats => ({
        logsProcessed: prevStats.logsProcessed + Math.floor(Math.random() * 100),
        totalEndpointsCapacity: 100,
        threatEvents: prevStats.threatEvents + (Math.random() > 0.95 ? 1 : 0),
        logsPerSecond: 12500 + Math.floor(Math.random() * 500),
        diskUsage: 76.0 + Math.random(),
        cpuLoad: 35 + Math.random() * 6
      }));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Cycle through active data flow animations
  useEffect(() => {
    const flowInterval = setInterval(() => {
      setActiveFlow(prev => (prev + 1) % 12);
    }, 800);

    return () => clearInterval(flowInterval);
  }, []);

  // Determines which flow line should be active based on the current activeFlow state
  const isActiveFlow = (flowIndex: number) => {
    return activeFlow === flowIndex;
  };

  // Function to show team member details
  const showTeamMemberDetails = (member) => {
    setSelectedTeamMember(member);
  };

  // Function to close team member details modal
  const closeTeamMemberDetails = () => {
    setSelectedTeamMember(null);
  };

  return (
    <div className="container mx-auto px-6 py-8">
      {/* HUD-Style Top Bar */}
      <div className="flex items-center justify-between mb-10 p-1 border border-teal-900/30 rounded-md bg-slate-800/30 backdrop-blur-sm">
        <div className="flex items-center space-x-4 px-4">
          <div className="text-teal-400 font-mono text-sm">
            <span className="text-slate-400">SYS:</span> ACTIVE
          </div>
          <div className="text-teal-400 font-mono text-sm">
            <span className="text-slate-400">LOG RATE:</span> {stats.logsPerSecond.toLocaleString()} PER SEC
          </div>
          <div className="text-teal-400 font-mono text-sm">
            <span className="text-slate-400">THREATS:</span> {stats.threatEvents} DETECTED
          </div>
        </div>
        <div className="flex items-center">
          <div className="h-2 bg-gradient-to-r from-teal-800 via-teal-500 to-teal-800 animate-pulse mx-4" style={{ width: '100px' }}></div>
          <div className="text-teal-400 font-mono text-xs px-4">
            <span className="text-slate-500">TIMESTAMP:</span> {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Dashboard Header */}
      <div className="mb-12">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold mb-2 flex items-center">
              <img src="https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/Copy%20of%20App-logo-animate-revised-white.gif" alt="Abhi" className="h-8 w-8 mr-3" />
              <span>AbhiStrike</span>
              <span className="text-teal-400 ml-2">Data Workflow</span>
              <div className="ml-3 h-2 w-2 bg-teal-500 rounded-full animate-pulse"></div>
            </h1>
            <p className="text-gray-400 max-w-3xl">
              Real-time visualization of security telemetry data flow from endpoints through AbhiStrike processing 
              infrastructure to the security operations dashboard.
            </p>
          </div>

          {/* Stats Card */}
          <div className="bg-slate-800/40 rounded-lg border border-teal-900/30 p-4 backdrop-blur-sm">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-sm text-slate-400">Logs Processed</div>
                <div className="text-xl font-bold text-teal-400">{stats.logsProcessed.toLocaleString()}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-slate-400">Total Endpoints Capacity</div>
                <div className="text-xl font-bold text-teal-400">{stats.totalEndpointsCapacity.toLocaleString()}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-slate-400">Cluster Health</div>
                <div className="flex items-center justify-center">
                  <span className="h-2 w-2 bg-teal-500 rounded-full animate-pulse mr-2"></span>
                  <span className="text-xl font-bold text-teal-400">98%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Workflow Visualization */}
      <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-6 backdrop-blur-sm shadow-xl mb-10 relative overflow-hidden">
        {/* Security scan line effect */}
        <div className="absolute left-0 right-0 h-px bg-teal-500/30 z-10" 
          style={{ 
            top: '50%',
            animation: 'scanHorizontal 20s infinite alternate linear',
            boxShadow: '0 0 10px rgba(20, 184, 166, 0.3), 0 0 20px rgba(20, 184, 166, 0.2)'
          }}>
        </div>

        {/* Grid background */}
        <div className="absolute inset-0 animated-grid opacity-20"></div>

        {/* Workflow Title */}
        <div className="mb-6 flex items-center justify-between z-10 relative">
          <div className="flex items-center space-x-2">
            <Activity className="h-5 w-5 text-teal-400" />
            <h2 className="text-xl font-bold">
              Live Data Flow Architecture
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            <span className="bg-teal-900/30 text-teal-400 text-xs px-3 py-1 rounded-full flex items-center border border-teal-900/30">
              <span className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-1.5 animate-pulse"></span>
              <span>Real-time Monitoring</span>
            </span>
          </div>
        </div>

        {/* Workflow Visualization - Using a 5-column grid layout */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 relative mb-4">
          {/* SVG Connectors */}
          <svg className="absolute inset-0 w-full h-full z-0" style={{ pointerEvents: 'none' }}>
            {/* Lines from Endpoints to AbhiStrike Workers */}
            <path 
              d="M290,120 C350,120 350,300 410,300" 
              fill="none" 
              stroke={isActiveFlow(0) ? "rgba(20, 184, 166, 0.8)" : "rgba(20, 184, 166, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M290,200 C350,200 350,320 410,320" 
              fill="none" 
              stroke={isActiveFlow(1) ? "rgba(20, 184, 166, 0.8)" : "rgba(20, 184, 166, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M290,280 C350,280 350,340 410,340" 
              fill="none" 
              stroke={isActiveFlow(2) ? "rgba(20, 184, 166, 0.8)" : "rgba(20, 184, 166, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M290,360 C350,360 350,360 410,360" 
              fill="none" 
              stroke={isActiveFlow(3) ? "rgba(20, 184, 166, 0.8)" : "rgba(20, 184, 166, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />

            {/* Lines from Workers to Master */}
            <path 
              d="M530,240 C550,240 550,240 570,240" 
              fill="none" 
              stroke={isActiveFlow(4) ? "rgba(20, 184, 166, 0.8)" : "rgba(20, 184, 166, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M530,280 C550,280 550,255 570,255" 
              fill="none" 
              stroke={isActiveFlow(5) ? "rgba(20, 184, 166, 0.8)" : "rgba(20, 184, 166, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M530,320 C550,320 550,270 570,270" 
              fill="none" 
              stroke={isActiveFlow(6) ? "rgba(20, 184, 166, 0.8)" : "rgba(20, 184, 166, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M530,360 C550,360 550,285 570,285" 
              fill="none" 
              stroke={isActiveFlow(7) ? "rgba(20, 184, 166, 0.8)" : "rgba(20, 184, 166, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M530,400 C550,400 550,300 570,300" 
              fill="none" 
              stroke={isActiveFlow(8) ? "rgba(20, 184, 166, 0.8)" : "rgba(20, 184, 166, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />

            {/* Lines from Master to Indexers */}
            <path 
              d="M690,240 C720,240 720,200 750,200" 
              fill="none" 
              stroke={isActiveFlow(9) ? "rgba(20, 184, 166, 0.8)" : "rgba(20, 184, 166, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M690,260 C720,260 720,260 750,260" 
              fill="none" 
              stroke={isActiveFlow(10) ? "rgba(20, 184, 166, 0.8)" : "rgba(20, 184, 166, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M690,280 C720,280 720,320 750,320" 
              fill="none" 
              stroke={isActiveFlow(11) ? "rgba(20, 184, 166, 0.8)" : "rgba(20, 184, 166, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />

            {/* Lines from Indexers to Dashboard */}
            <path 
              d="M870,200 C900,200 900,260 930,260" 
              fill="none" 
              stroke="rgba(20, 184, 166, 0.3)" 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M870,260 C900,260 900,260 930,260" 
              fill="none" 
              stroke="rgba(20, 184, 166, 0.3)" 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M870,320 C900,320 900,260 930,260" 
              fill="none" 
              stroke="rgba(20, 184, 166, 0.3)" 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />

            {/* Data Particles */}
            {isActiveFlow(0) && (
              <circle cx="350" cy="120" r="3" fill="#14b8a6" className="data-particle">
                <animateMotion
                  path="M0,0 C60,0 60,180 120,180"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            )}
            {isActiveFlow(5) && (
              <circle cx="540" cy="280" r="3" fill="#14b8a6" className="data-particle">
                <animateMotion
                  path="M0,0 C20,0 20,-25 40,-25"
                  dur="0.5s"
                  repeatCount="indefinite"
                />
              </circle>
            )}
            {isActiveFlow(10) && (
              <circle cx="710" cy="260" r="3" fill="#14b8a6" className="data-particle">
                <animateMotion
                  path="M0,0 C30,0 30,0 60,0"
                  dur="0.5s"
                  repeatCount="indefinite"
                />
              </circle>
            )}
          </svg>

          {/* Column 1: Endpoints */}
          <div className="md:col-span-1">
            <div className="bg-slate-800/70 border border-slate-700 rounded-lg p-4 backdrop-blur-sm mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <Wifi className="h-4 w-4 mr-2 text-teal-400" />
                  Endpoints
                </h3>
                <span className="bg-teal-900/30 text-teal-400 text-xs px-2 py-0.5 rounded-full flex items-center border border-teal-900/30">
                  <span className="w-1 h-1 bg-teal-500 rounded-full mr-1 animate-pulse"></span>
                  <span>Connected</span>
                </span>
              </div>

              <div className="space-y-4">
                {nodes.endpoints.map((endpoint, index) => (
                  <motion.div 
                    key={endpoint.id}
                    className="bg-slate-900/40 p-3 rounded-lg border border-slate-700/80 hover:border-teal-900/30 transition-colors group"
                    whileHover={{ y: -3, transition: { duration: 0.2 } }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-slate-900/70 rounded-lg border border-slate-700">
                        {endpoint.type === 'Server' && <Server className="h-5 w-5 text-blue-400" />}
                        {endpoint.type === 'Desktop' && <Monitor className="h-5 w-5 text-green-400" />}
                        {endpoint.type === 'Laptop' && <Laptop className="h-5 w-5 text-purple-400" />}
                        {endpoint.type === 'Virtual Machine' && <HardDrive className="h-5 w-5 text-yellow-400" />}
                      </div>
                      <div>
                        <h4 className="font-medium text-white">{endpoint.type}</h4>
                        <div className="text-xs text-slate-400">{endpoint.ip}</div>
                        <div className="flex items-center mt-1 text-xs">
                          <span className="text-teal-400 flex items-center">
                            <span className="h-1 w-1 bg-teal-500 rounded-full mr-1 animate-pulse"></span>
                            Connected
                          </span>
                          <span className="mx-1.5">•</span>
                          <span className="text-teal-400">{endpoint.logCount.toLocaleString()} logs</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-teal-800 via-teal-500 to-teal-600 rounded-full"
                        initial={{ width: '0%' }}
                        animate={{ width: '100%' }}
                        transition={{ 
                          repeat: Infinity, 
                          duration: 2 + index, 
                          ease: "easeInOut" 
                        }}
                      ></motion.div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Column 2: AbhiStrike Node */}
          <div className="md:col-span-1">
            <div className="bg-slate-800/70 border border-slate-700 rounded-lg p-4 backdrop-blur-sm mb-6 h-full">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <Server className="h-4 w-4 mr-2 text-teal-400" />
                  AbhiStrike Nodes
                </h3>
                <span className="bg-teal-900/30 text-teal-400 text-xs px-2 py-0.5 rounded-full flex items-center border border-teal-900/30">
                  <span className="w-1 h-1 bg-teal-500 rounded-full mr-1 animate-pulse"></span>
                  <span>Active</span>
                </span>
              </div>

              <div className="space-y-3">
                {nodes.AbhiStrikeWorkers.map((worker, index) => (
                  <motion.div 
                    key={worker.id}
                    className={`bg-slate-900/40 p-3 rounded-lg border ${worker.status === 'high-load' ? 'border-yellow-900/30' : 'border-slate-700/80'} hover:border-teal-900/30 transition-colors`}
                    whileHover={{ y: -3, transition: { duration: 0.2 } }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 ${worker.status === 'high-load' ? 'bg-yellow-950/20' : 'bg-slate-900/70'} rounded-lg border ${worker.status === 'high-load' ? 'border-yellow-900/30' : 'border-slate-700'}`}>
                        <Server className={`h-5 w-5 ${worker.status === 'high-load' ? 'text-yellow-400' : 'text-teal-400'}`} />
                      </div>
                      <div>
                        <h4 className="font-medium text-white text-sm">{worker.name}</h4>
                        <div className="text-xs text-slate-400">{worker.ip}</div>
                        <div className="flex items-center mt-1 text-xs">
                          {worker.status === 'operational' ? (
                            <span className="text-green-400 flex items-center">
                              <span className="h-1 w-1 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                              Operational
                            </span>
                          ) : (
                            <span className="text-yellow-400 flex items-center">
                              <span className="h-1 w-1 bg-yellow-500 rounded-full mr-1 animate-pulse"></span>
                              High Load
                            </span>
                          )}
                          <span className="mx-1.5">•</span>
                          <span className={`${worker.status === 'high-load' ? 'text-yellow-400' : 'text-blue-400'}`}>{worker.load}% load</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${worker.status === 'high-load' ? 'bg-gradient-to-r from-yellow-900 via-yellow-600 to-yellow-700' : 'bg-gradient-to-r from-green-900 via-green-600 to-green-700'}`}
                        style={{ width: `${worker.load}%` }}
                      ></div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Column 3: Abhi AI */}
          <div className="md:col-span-1">
            <div className="bg-slate-800/70 border border-slate-700 rounded-lg p-4 backdrop-blur-sm mb-6 h-full">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <Cpu className="h-4 w-4 mr-2 text-red-500" />
                  Abhi AI
                </h3>
                <span className="bg-green-900/30 text-green-400 text-xs px-2 py-0.5 rounded-full flex items-center border border-green-900/30">
                  <span className="w-1 h-1 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                  <span>AI Core</span>
                </span>
              </div>

              <motion.div 
                className="bg-teal-950/10 p-4 rounded-lg border border-teal-900/30 hover:border-teal-700/50 transition-colors relative overflow-hidden"
                whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
              >
                {/* Central processing animation */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-teal-900/0 via-teal-700/10 to-teal-900/0 animate-pulse"></div>
                
                <div className="flex justify-center items-center mb-4">
                  <div className="p-3 bg-black/60 rounded-full border border-teal-900/50 shadow-lg shadow-teal-900/20">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ 
                        repeat: Infinity, 
                        duration: 10, 
                        ease: "linear" 
                      }}
                    >
                      <Cpu className="h-10 w-10 text-teal-500" />
                    </motion.div>
                  </div>
                </div>
                
                <div className="text-center mb-3">
                  <h4 className="font-bold text-white text-lg">{nodes.master.name}</h4>
                  <div className="text-sm text-teal-400">{nodes.master.ip}</div>
                  
                  <div className="flex justify-center items-center space-x-2 mt-2">
                    <span className="text-green-400 text-sm flex items-center">
                      <span className="h-1.5 w-1.5 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                      Operational
                    </span>
                    <span className="text-slate-500">|</span>
                    <span className="text-teal-400 text-sm">{nodes.master.load}% Load</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-black/50 p-2 rounded border border-teal-900/20 text-center">
                    <div className="text-xs text-slate-400">Data Processing</div>
                    <div className="text-sm font-bold text-teal-400">{nodes.master.dataRate}</div>
                  </div>
                  <div className="bg-black/50 p-2 rounded border border-teal-900/20 text-center">
                    <div className="text-xs text-slate-400">Uptime</div>
                    <div className="text-sm font-bold text-green-400">99.99%</div>
                  </div>
                </div>
                
                <div className="mt-3 h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-gradient-to-r from-teal-900 via-teal-600 to-teal-900 rounded-full relative"
                    initial={{ width: '0%' }}
                    animate={{ width: '90%' }}
                    transition={{ 
                      repeat: Infinity, 
                      duration: 3, 
                      ease: "easeInOut", 
                      repeatType: "reverse" 
                    }}
                  >
                    <div className="absolute top-0 left-0 h-full w-20 bg-gradient-to-r from-transparent via-teal-300/30 to-transparent animate-pulse"></div>
                  </motion.div>
                </div>
                
                <div className="mt-3 text-xs text-center text-slate-500">
                  Abhi AI Core G1 Engine
                </div>
              </motion.div>
            </div>
          </div>
          
          {/* Column 4: Indexers */}
          <div className="md:col-span-1">
            <div className="bg-slate-800/70 border border-slate-700 rounded-lg p-4 backdrop-blur-sm mb-6 h-full">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <Database className="h-4 w-4 mr-2 text-red-500" />
                  Indexers
                </h3>
                <span className="bg-green-900/30 text-green-400 text-xs px-2 py-0.5 rounded-full flex items-center border border-green-900/30">
                  <span className="w-1 h-1 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                  <span>Storage</span>
                </span>
              </div>

              <div className="space-y-3">
                {nodes.indexers.map((indexer, index) => (
                  <motion.div 
                    key={indexer.id}
                    className="bg-slate-900/40 p-3 rounded-lg border border-slate-700/80 hover:border-teal-900/30 transition-colors"
                    whileHover={{ y: -3, transition: { duration: 0.2 } }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-teal-950/20 rounded-lg border border-teal-900/30">
                        <Database className="h-5 w-5 text-teal-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-white text-sm">{indexer.name}</h4>
                        <div className="text-xs text-slate-400">{indexer.ip}</div>
                        <div className="flex items-center mt-1 text-xs">
                          <span className="text-green-400 flex items-center">
                            <span className="h-1 w-1 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                            Operational
                          </span>
                          <span className="mx-1.5">•</span>
                          <span className="text-teal-400">{indexer.indexRate}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-teal-900 via-teal-600 to-teal-900 rounded-full"
                        animate={{ 
                          width: ['60%', '80%', '70%', '90%', '75%'] 
                        }}
                        transition={{ 
                          repeat: Infinity, 
                          duration: 5, 
                          times: [0, 0.2, 0.5, 0.8, 1] 
                        }}
                      ></motion.div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Column 5: Dashboard & Users */}
          <div className="md:col-span-1">
            <div className="bg-slate-800/70 border border-slate-700 rounded-lg p-4 backdrop-blur-sm mb-6 h-full">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <Activity className="h-4 w-4 mr-2 text-red-500" />
                  Dashboard
                </h3>
                <span className="bg-green-900/30 text-green-400 text-xs px-2 py-0.5 rounded-full flex items-center border border-green-900/30">
                  <span className="w-1 h-1 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                  <span>Online</span>
                </span>
              </div>

              <motion.div 
                className="bg-slate-900/60 p-4 rounded-lg border border-slate-700 hover:border-teal-900/30 transition-colors mb-4"
                whileHover={{ y: -3, transition: { duration: 0.2 } }}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 }}
              >
                <div className="flex items-start space-x-3 mb-3">
                  <div className="p-2 bg-teal-950/20 rounded-lg border border-teal-900/30">
                    <Activity className="h-5 w-5 text-teal-500" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white">{nodes.dashboard.name}</h4>
                    <div className="text-xs text-slate-400">{nodes.dashboard.ip}</div>
                    <div className="flex items-center mt-1 text-xs">
                      <span className="text-green-400 flex items-center">
                        <span className="h-1 w-1 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                        Operational
                      </span>
                      <span className="mx-1.5">•</span>
                      <span className="text-teal-400">{nodes.dashboard.activeUsers} active users</span>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-slate-900/40 p-2 rounded border border-slate-700 text-center">
                    <div className="text-xs text-slate-400">Queries</div>
                    <div className="text-sm font-bold text-teal-400">283/min</div>
                  </div>
                  <div className="bg-slate-900/40 p-2 rounded border border-slate-700 text-center">
                    <div className="text-xs text-slate-400">Response</div>
                    <div className="text-sm font-bold text-green-400">124ms</div>
                  </div>
                  <div className="bg-slate-900/40 p-2 rounded border border-slate-700 text-center">
                    <div className="text-xs text-slate-400">Load</div>
                    <div className="text-sm font-bold text-green-400">32%</div>
                  </div>
                </div>
                
                <div className="mt-3 h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-gradient-to-r from-teal-900 via-teal-600 to-teal-900 rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: '90%' }}
                    transition={{ 
                      repeat: Infinity, 
                      duration: 4, 
                      ease: "easeInOut", 
                      repeatType: "reverse" 
                    }}
                  ></motion.div>
                </div>
              </motion.div>

              <motion.div 
                className="bg-slate-900/60 p-4 rounded-lg border border-slate-700 hover:border-teal-900/30 transition-colors"
                whileHover={{ y: -3, transition: { duration: 0.2 } }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-white flex items-center">
                    <UserCircle className="h-4 w-4 mr-2 text-teal-400" />
                    Security Analysts
                  </h4>
                  <span className="bg-teal-900/30 text-teal-400 text-xs px-2 py-0.5 rounded-full border border-teal-900/30">
                    {nodes.dashboard.activeUsers} Online
                  </span>
                </div>

                <div className="grid grid-cols-4 gap-2">
                  {[...Array(2)].map((_, index) => (
                    <motion.div 
                      key={index}
                      className="bg-slate-900/40 p-2 rounded-lg border border-slate-700 flex flex-col items-center text-center"
                      whileHover={{ y: -2, transition: { duration: 0.2 } }}
                      animate={{ 
                        y: [0, -3, 0],
                      }}
                      transition={{ 
                        repeat: Infinity, 
                        duration: 2 + index * 0.5, 
                        delay: index * 0.2,
                        ease: "easeInOut" 
                      }}
                    >
                      <div className="h-8 w-8 bg-gradient-to-br from-slate-700 to-slate-900 rounded-full border border-slate-700 mb-1 flex items-center justify-center">
                        <UserCircle className="h-5 w-5 text-teal-400" />
                      </div>
                      <div className="text-xs text-slate-400">Analyst</div>
                      <div className="text-xs mt-1">
                        <span className="inline-block h-1.5 w-1.5 bg-green-500 rounded-full mr-1"></span>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-3 flex justify-center">
                  <span className="text-xs text-slate-500 flex items-center">
                    <Lock className="h-3 w-3 mr-1 text-green-500" />
                    Secure Access Protocol
                  </span>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* System Performance Indicators */}
        <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-4 backdrop-blur-sm">
          <h3 className="font-bold mb-4 flex items-center text-sm">
            <AlertTriangle className="h-4 w-4 mr-2 text-teal-500" />
            System Performance Metrics
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="bg-slate-900/60 p-3 rounded-lg border border-slate-700">
              <div className="text-xs text-slate-400 mb-1">CPU Load</div>
              <div className="text-xl font-bold text-teal-400">{stats.cpuLoad.toFixed(1)}%</div>
              <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-teal-600 rounded-full" style={{ width: `${stats.cpuLoad}%` }}></div>
              </div>
            </div>
            
            <div className="bg-slate-900/60 p-3 rounded-lg border border-slate-700">
              <div className="text-xs text-slate-400 mb-1">Memory</div>
              <div className="text-xl font-bold text-cyan-400">62.3%</div>
              <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-cyan-600 rounded-full" style={{ width: '62.3%' }}></div>
              </div>
            </div>
            
            <div className="bg-slate-900/60 p-3 rounded-lg border border-slate-700">
              <div className="text-xs text-slate-400 mb-1">Disk Usage</div>
              <div className="text-xl font-bold text-green-400">{stats.diskUsage.toFixed(1)}%</div>
              <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-green-700 rounded-full" style={{ width: `${stats.diskUsage}%` }}></div>
              </div>
            </div>
            
            <div className="bg-slate-900/60 p-3 rounded-lg border border-slate-700">
              <div className="text-xs text-slate-400 mb-1">Network</div>
              <div className="text-xl font-bold text-teal-400">421 MB/s</div>
              <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                <motion.div 
                  className="h-full bg-teal-600 rounded-full"
                  animate={{ width: ['40%', '70%', '50%', '90%', '60%'] }}
                  transition={{ 
                    repeat: Infinity, 
                    duration: 5,
                    times: [0, 0.25, 0.5, 0.75, 1] 
                  }}
                ></motion.div>
              </div>
            </div>
            
            <div className="bg-slate-900/60 p-3 rounded-lg border border-slate-700">
              <div className="text-xs text-slate-400 mb-1">Response Time</div>
              <div className="text-xl font-bold text-cyan-400">124 ms</div>
              <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-700 to-cyan-500 rounded-full" style={{ width: '30%' }}></div>
              </div>
            </div>
            
            <div className="bg-slate-900/60 p-3 rounded-lg border border-slate-700">
              <div className="text-xs text-slate-400 mb-1">Security Score</div>
              <div className="text-xl font-bold text-green-400">98%</div>
              <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-700 to-green-500 rounded-full" style={{ width: '98%' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Team Member Details Modal */}
      {selectedTeamMember && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" onClick={closeTeamMemberDetails}>
          <motion.div 
            className="bg-gradient-to-b from-slate-900 to-slate-950 border border-slate-700 rounded-lg p-6 max-w-md w-full shadow-2xl relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-900 via-teal-600 to-teal-900"></div>
            
            <div className="flex items-start mb-6">
              <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-teal-700/30 mr-4">
                <img src={selectedTeamMember.avatar} alt={selectedTeamMember.name} className="w-full h-full object-cover" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{selectedTeamMember.name}</h3>
                <p className="text-teal-400">{selectedTeamMember.role}</p>
                
                <div className="flex items-center mt-2">
                  <span className={`text-xs ${
                    selectedTeamMember.shift === 'Morning' ? 'text-yellow-400' : 
                    selectedTeamMember.shift === 'Evening' ? 'text-orange-400' : 
                    selectedTeamMember.shift === 'Night' ? 'text-blue-400' : 
                    'text-teal-400'
                  } flex items-center`}>
                    <span className="h-1.5 w-1.5 bg-teal-500 rounded-full mr-1 animate-pulse"></span>
                    <span>{selectedTeamMember.shift === '24/7' ? '24/7 Available' : `${selectedTeamMember.shift} Shift`}</span>
                  </span>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-slate-900/40 p-4 rounded-lg border border-slate-700">
                <div className="flex items-center mb-2">
                  <Mail className="h-4 w-4 text-teal-500 mr-2" />
                  <h4 className="text-sm font-medium text-slate-300">Email</h4>
                </div>
                <p className="text-white">{selectedTeamMember.email}</p>
              </div>
              
              <div className="bg-slate-900/40 p-4 rounded-lg border border-slate-700">
                <div className="flex items-center mb-2">
                  <Phone className="h-4 w-4 text-teal-500 mr-2" />
                  <h4 className="text-sm font-medium text-slate-300">Phone</h4>
                </div>
                <p className="text-white">{selectedTeamMember.phone}</p>
              </div>
              
              <div className="bg-slate-900/40 p-4 rounded-lg border border-slate-700">
                <div className="flex items-center mb-2">
                  <Clock className="h-4 w-4 text-teal-500 mr-2" />
                  <h4 className="text-sm font-medium text-slate-300">Shift Hours</h4>
                </div>
                <p className="text-white">
                  {selectedTeamMember.shift === 'Morning' ? '08:00 - 16:00 PKT' : 
                   selectedTeamMember.shift === 'Evening' ? '16:00 - 00:00 PKT' : 
                   selectedTeamMember.shift === 'Night' ? '00:00 - 08:00 PKT' : 
                   '24/7 On Call'}
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex justify-center">
              <button 
                className="px-4 py-2 bg-teal-900/40 hover:bg-teal-800/60 border border-teal-700/40 rounded-md text-white transition-all"
                onClick={closeTeamMemberDetails}
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DataWorkflow;
